import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Camera, Palette, Sparkles, ShieldCheck, Clock, Hammer, ArrowRight } from 'lucide-react';
import { useT } from '../studioI18n';
import { useStudioAuth } from '../StudioAuthContext';
import { Page, MotionButton, stagger, rise } from '../components/Motion';
import { BeforeAfter } from '../components/BeforeAfter';

export const HERO_BEFORE = 'https://static.prod-images.emergentagent.com/jobs/11181bb4-7f06-4317-a03c-b102e454ffd1/images/6e6f9d901ae574fdea3c7c823724f171203b0bca8a582863a807a53ac2bba664.jpeg';
export const HERO_AFTER = 'https://static.prod-images.emergentagent.com/jobs/11181bb4-7f06-4317-a03c-b102e454ffd1/images/c122dffa8df1a5916278d9c3c9d5668836939f9e7811ac682ab1839051845c3d.jpeg';

const Step = ({ n, icon: Icon, title, desc, testId }) => (
  <motion.div variants={rise} whileHover={{ y: -6 }} transition={{ type: 'spring', stiffness: 300, damping: 22 }} className="st-glass rounded-[1.75rem] p-6 relative overflow-hidden" data-testid={testId}>
    <span className="absolute -right-3 -top-4 text-[88px] font-display font-extrabold text-teal-600/10 leading-none select-none">{n}</span>
    <span className="h-12 w-12 rounded-2xl bg-teal-600 text-white flex items-center justify-center shadow-lg shadow-teal-600/30"><Icon className="h-6 w-6" /></span>
    <h3 className="font-display font-bold text-lg text-slate-900 mt-4">{title}</h3>
    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{desc}</p>
  </motion.div>
);

export default function LandingPage() {
  const { t } = useT();
  const { customer } = useStudioAuth();
  const navigate = useNavigate();
  const go = () => navigate(customer ? '/project' : '/auth?mode=signup');

  return (
    <Page testId="studio-landing">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-12 grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 items-center">
        <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
          <motion.p variants={rise} className="text-xs font-bold uppercase tracking-[0.25em] text-teal-700">{t.heroKicker}</motion.p>
          <motion.h1 variants={rise} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05]" data-testid="hero-title">{t.heroTitle}</motion.h1>
          <motion.p variants={rise} className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">{t.heroSub}</motion.p>
          <motion.div variants={rise} className="flex flex-wrap items-center gap-3 pt-2">
            <MotionButton onClick={go} className="text-base px-7" data-testid="hero-cta-start-btn">{t.start} <ArrowRight className="h-5 w-5" /></MotionButton>
            {!customer && <button type="button" onClick={() => navigate('/auth')} className="h-13 px-5 text-sm font-bold text-teal-800 hover:underline" data-testid="hero-login-link">{t.login}</button>}
          </motion.div>
          <motion.ul variants={rise} className="flex flex-wrap gap-x-5 gap-y-2 text-xs font-semibold text-slate-600 pt-2">
            <li className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-teal-700" /> {t.trust1}</li>
            <li className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-teal-700" /> {t.trust2}</li>
            <li className="flex items-center gap-1.5"><Hammer className="h-4 w-4 text-teal-700" /> {t.trust3}</li>
          </motion.ul>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.94, rotate: -1 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.15 }} className="relative">
          <div className="absolute -inset-6 rounded-[2.5rem] bg-teal-400/20 blur-3xl st-float" />
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-white/70">
            <BeforeAfter before={HERO_BEFORE} after={HERO_AFTER} labels={{ before: t.heroBefore, after: t.heroAfter }} aspect="3 / 2" testId="hero-before-after" />
          </div>
          <p className="text-center text-xs text-slate-500 mt-3">{t.heroHint}</p>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
        <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-base md:text-lg font-bold text-slate-900 mb-5">{t.howTitle}</motion.h2>
        <motion.div variants={stagger} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.3 }} className="grid md:grid-cols-3 gap-4 sm:gap-6">
          <Step n="1" icon={Camera} title={t.step1T} desc={t.step1D} testId="how-it-works-card-1" />
          <Step n="2" icon={Palette} title={t.step2T} desc={t.step2D} testId="how-it-works-card-2" />
          <Step n="3" icon={Sparkles} title={t.step3T} desc={t.step3D} testId="how-it-works-card-3" />
        </motion.div>
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mt-10 flex justify-center">
          <MotionButton onClick={go} variant="dark" className="px-8" data-testid="landing-bottom-cta">{t.start} <ArrowRight className="h-5 w-5" /></MotionButton>
        </motion.div>
      </section>
    </Page>
  );
}
